import { version } from '../../package.json';
import { getAnalyticsPage, User, analyticsSeriesName, getStorageItem, getFullUrl, getUrl } from './';

const Echo = (window as any).echo_js; // TODO: Switch to Echo import

const EchoClient = Echo.EchoClient,
  Media = Echo.Media,
  Enums = Echo.Enums,
  LabelKeys = Echo.Enums.EchoLabelKeys,
  ConfigKeys = Echo.ConfigKeys;

const sequentialEventTimeSpacing = 100;
const AppName = "bbcplayer";
const AdvertiserId = "BBCPlayer~Web";
const Language = "en-uk";
const Destination = import.meta.env.VITE_WEB_ENVIRONMENT === "production" ? Enums.Destinations.BBC_STUDIOS_PLAYER : Enums.Destinations.BBC_STUDIOS_PLAYER_TEST;
const DebugMode = process.env.NODE_ENV !== "production" && "info";

enum EventType {
  Impression = 1,
  Click = 0
}

enum ActionType {
  Select = "select",
  Error = "error",
  UserStateChange = "user_state_change",
  Module = "module",
  Complete = "complete",
  Next = "next",
  None = "none"
}

enum CampaignID {
  Application = "Application",
  Error = "Error",
  Page = "Page",
  Player = "Player"
};

enum Label {
  CampaignID = "container",
  Variant = "personalisation",
  Format = "metadata",
  AdvertiserId = "source",
  Url = "result",
  IsBackground = "is_background"
};

enum UserStatus {
  Unauthenticated = "unauth",
  FreeTrail = "freetrial",
  Subscribed = "subscribed",
  Unsubscribed = "unsubscribed",
  FreeTrialExpired = "freetrial_expired"
};

enum ContentType {
  Account = "account",
  Channel = "channels",
  Home = "index-home",
  Category = "index-category",
  Admin = "admin",
  Brand = "brand",
  Search = "search",
  List = "list-curated",
  Episode = "episode",
  TvGuide = "live",
  LivePlayer = "live"
};

enum CustomOperatorName {
  starhub = "STARHUB",
  singtel = "SINGTEL",
  tm = "TM",
  accOrg = "account_organisation"
}

const getUserStatus = async () => {
  if(!User.authenticated())
    return UserStatus.Unauthenticated;
  else if(User.hasActiveSubscription())
    return UserStatus.Subscribed;
  else if(await User.hasExpiredFreeTrial())
    return UserStatus.FreeTrialExpired;
  else if(User.hasFreeTrial())
    return UserStatus.FreeTrail;
  else if(!User.hasActiveSubscription())
    return UserStatus.Unsubscribed;
};

const wait = async (fn) => setTimeout(fn, sequentialEventTimeSpacing);

class Analytics {
  private echo;
  private player;
  private startTime;
  private playbackStarted = false;
  private static readonly navigationContextNames = {
    logo: "logo",
    home: "home",
    channel: "channel",
    category: "category",
    live: "live",
    more: "more",
    tvGuide: "tv_guide",
    footer: "footer"
  } as const;
  private currentUrl: string = window.location.href;
  private previousUrl: string = document.referrer || "";

  private normalizeWhitespace = (value: string) => value.trim().replace(/\s+/g, " ");

  private formatItemName = (value: string, spaceReplacement: "-" | "_" | " " = "-") => {
    const normalized = this.normalizeWhitespace(value).toLowerCase();
    if (spaceReplacement === " ") return normalized;
    return normalized.replace(/\s+/g, spaceReplacement);
  }

  private getNavigationItemName = (value: string, context?: typeof Analytics.navigationContextNames[keyof typeof Analytics.navigationContextNames]) => {
    const normalized = this.normalizeWhitespace(value).toLowerCase();
    switch (context) {
      case Analytics.navigationContextNames.home:
        return "home";
      case Analytics.navigationContextNames.tvGuide:
        return "tv guide";
      case Analytics.navigationContextNames.more:
        if (normalized === "sign in") return "sign_in";
        return this.formatItemName(value, "_");
      case Analytics.navigationContextNames.channel:
      case Analytics.navigationContextNames.category:
      case Analytics.navigationContextNames.live:
        return this.formatItemName(value, "-");
      default:
        return this.formatItemName(value, "-");
    }
  }

  private getNavigationGroupName = (context?: typeof Analytics.navigationContextNames[keyof typeof Analytics.navigationContextNames]) => {
    switch (context) {
      case Analytics.navigationContextNames.logo:
        return "logo~select";
      case Analytics.navigationContextNames.home:
        return "home~select";
      case Analytics.navigationContextNames.channel:
        return "channel~select";
      case Analytics.navigationContextNames.category:
        return "category~select";
      case Analytics.navigationContextNames.live:
        return "live~select";
      case Analytics.navigationContextNames.tvGuide:
      case Analytics.navigationContextNames.more:
        return "more~select";
      case Analytics.navigationContextNames.footer:
        return "footer~select";
      default:
        return undefined;
    }
  }

  private initEchoClient = () => {
    this.echo = new EchoClient(this.getAppName(), Enums.ApplicationType.WEB, {
      [ConfigKeys.DEBUG_MODE]: DebugMode,
      [ConfigKeys.DESTINATION]: Destination
    });
    this.echo.setAppVersion(version);
  }

  constructor() {
    this.initEchoClient();
  }

  updateAppName = () => {
    this.initEchoClient();
  }

  private setMediaPlayerAttributes = () =>{
    var mediaPlayer = new Echo.MediaPlayer();
    this.echo.setMediaPlayer(mediaPlayer);
    this.echo.setPlayerName(AppName);
    this.echo.setPlayerVersion(version);
  }

  private getAppName = () => {
    const providerCountry = getStorageItem("country", true);

    return providerCountry === "SG" ? "bbcplayer_singapore" : "bbcplayer_malaysia";
  }

  private getCustomOrgName = (provider) =>{
    switch (User.providerCountry()){
      case "MY":
        return CustomOperatorName.tm;
      case "SG":
        return (provider === "STARHUB") ? CustomOperatorName.starhub : CustomOperatorName.singtel;
      default:
        return provider;
    }
  }

  private setAccountOrganisation =()=>{
    if (User.authenticated() && User.id()){
      try {
        this.echo.addProperty(CustomOperatorName.accOrg, this.getCustomOrgName(User.provider())?.toUpperCase());
      }catch(err) {
        console.log(err)
      }
    }else{
      try {
        this.echo.removeProperty(CustomOperatorName.accOrg);
        this.echo.removeProperty('user_id');
      }catch(err) {
        console.log(err)
      }
    }
  }

  private setAvChannel =(isLiveVideo, channel = "")=>{
    if (isLiveVideo){
      try {
        this.echo.addProperty("av_channel", channel);
      }catch(err) {
        console.log(err)
      }
    }else{
      try {
        this.echo.removeProperty("av_channel");
      }catch(err) {
        console.log(err)
      }
    }
  }

  // STANDARD EVENTS

  private userActionEvent = (actionName: string, type: ActionType, eventType: EventType = EventType.Click, eventLabels = {}, includeStandardLabels = true) => {
    // need to uncomment it later and fix analytic issues due to change in data structure
    eventLabels[Label.IsBackground] = Boolean(eventType);
    eventLabels[LabelKeys.REFERRER_URL] = this.getReferrerUrl();

    if(includeStandardLabels) eventLabels[Label.AdvertiserId] = AdvertiserId;
    const actionType = type !== ActionType.None && type;
    this.setAccountOrganisation();
    try { this.echo.userActionEvent(actionType, actionName, eventLabels);}
    catch(err) { console.log(err) }
  }

  private pageViewEvent = async (page: String, contentType: ContentType, metadata = {}) => {
    // need to uncomment it later and fix analytic issues due to change in data structure
    metadata = {
      ...metadata,
      "language": Language,
      [LabelKeys.CONTENT_TYPE]: contentType,
      [LabelKeys.AV_CONTENT_TYPE]:contentType,
      [LabelKeys.CUSTOM_VAR_1]: await getUserStatus(),
      "product_platform": Enums.ApplicationType.WEB,
      [LabelKeys.CUSTOM_VAR_3]: getStorageItem("operator", true) || "",
      [LabelKeys.REFERRER_URL]: this.getReferrerUrl()
    };

    this.setAccountOrganisation();
    try { this.echo.viewEvent(page, metadata) }
    catch(err) { console.log(err) }
  }

  private setCounterName = (name) => {
    try { this.echo.setCounterName(name) }
    catch(err) { console.log(err) }
  }

  private buildPageViewMetadata = ({
    contentType,
    groupName,
    itemName,
    extraMetadata = {}
  }: {
    contentType?: string;
    groupName?: string;
    itemName?: string;
    extraMetadata?: Record<string, any>;
  }) => ({
    group_name: groupName,
    item_name: itemName,
    ...(contentType ? { [LabelKeys.CONTENT_TYPE]: contentType } : {}),
    ...extraMetadata
  });


  // USER STATUS EVENTS

  userStatusLoggedIn = (id) => {
    this.echo.setLoggedInToBBCId(id);
  }

  userStatusLoggedOut = () => {
    this.echo.setLoggedOutOfBBCId();
  }

  // PAGE VIEW EVENTS
  pageViewHome = () => {
    this.pageViewEvent(getAnalyticsPage(), ContentType.Home);
  }

  pageViewBasic = () => {
    this.pageViewEvent(getAnalyticsPage(), ContentType.Admin);
  }

  pageViewChannel = (channel) => {
    this.pageViewEvent(getAnalyticsPage(), ContentType.Channel, {
      [LabelKeys.CHAPTER_1]: channel
    });
  }

  pageViewCategory = (category) => {
    this.pageViewEvent(getAnalyticsPage(), ContentType.Category, {
      [LabelKeys.CHAPTER_1]: category
    });
  }

  pageViewList = () => {
    this.pageViewEvent(getAnalyticsPage(), ContentType.List);
  }

  pageViewSettings = () => {
    this.pageViewEvent(getAnalyticsPage(), ContentType.Account);
  }

  pageViewPinSuccess = () => {
    this.pageViewEvent("player.settings_pin_success.page", ContentType.Account);
  }

  pageViewSignIn = () => {
    this.pageViewEvent("player.sign_in.page", ContentType.Account);
  }

  pageViewTvGuide = () => {
    this.pageViewEvent("player.tvguide.page", ContentType.TvGuide);
  }

  pageViewLivePlayer = () => {
    this.pageViewEvent("player.live.page", ContentType.LivePlayer);
  }

  private pageViewSubscribed = () => {
    this.pageViewEvent("player.signed_in.page", ContentType.Account);
  }

  private pageViewTrialStarted = () => {
    this.pageViewEvent("player.free_trial_start.page", ContentType.Account);
  }

  private pageViewTrialEnding = () => {
    this.pageViewEvent("player.free_trial_ending.page", ContentType.Account);
  }

  pageViewSearch = async (keyword = "", resultsCount = 0) => {
    await this.pageViewEvent("player.search.page", ContentType.Account);
    this.pageViewCustomEvent({
      ise_keyword: keyword,
      ise_page: Number(resultsCount > 0)
    }, true);
  }

  pageViewSeries = (series, season) => {
    const { extId, seasonNumber } = season;
    const seriesName = analyticsSeriesName(series.slug || series.uri);

    this.pageViewEvent(getAnalyticsPage(), ContentType.Brand, {
      [LabelKeys.SECTION]: `${seriesName}::s${seasonNumber}`,
      [LabelKeys.CONTENT_ID]: extId || "",
    });
  }

  pageViewEpisodePlayback = async (episode, isLive = false) => {
    const { extId, series, episodeNumber, seasonNumber, seriesNumber } = episode;
    const seriesName = isLive ? episode.name.toLowerCase() : analyticsSeriesName(series.slug || series.uri);
    const page = `player.${seriesName}.season_${seasonNumber || seriesNumber}.episode_${episodeNumber}.page`;

    await this.pageViewEvent(page, ContentType.Episode, {
      [LabelKeys.CHAPTER_1]: isLive ? episode.name : series.name,
      [LabelKeys.SECTION]: `${seriesName}::s${seasonNumber || seriesNumber}::e${episodeNumber}`,
      [LabelKeys.CONTENT_ID]: extId || "",
      [LabelKeys.AV_BROADCASTING_TYPE]: isLive ? Enums.avBroadcastingType.LIVE: Enums.avBroadcastingType.CLIP,
    });
  }

  // USER ACTION EVENTS

  userActionSelectContentFromRail = (
    title,
    position,
    url,
    clickedItemName?: string,
    railSelectionData: {
      item_name?: string;
      group_name?: string;
      group_item_count?: number | string;
      item_position?: number | string;
      group_position?: number | string;
      results_count?: number;
    } = {}
  ) => {
    if (title.includes("search")) {
      this.userActionCustomEvent("internal_search_result", "click", {
        ise_keyword: clickedItemName,
        ise_page: Number((railSelectionData?.results_count ?? 0) > 0),
        item_name: railSelectionData.item_name || "",
        item_link: url,
        ise_click_rank: Number(railSelectionData.item_position) || 0
      })
    } else {
      this.userActionCustomEvent("viewability", "select", {
        item_name: railSelectionData.item_name || "",
        group_name: railSelectionData.group_name || "",
        group_item_count: railSelectionData.group_item_count || "",
        item_link: url,
        item_position: railSelectionData.item_position || "",
        group_position: railSelectionData.group_position || ""
      });
    }
  }

  // Map API response structure to analytics expected structure
  private normalizeEpisodeData = (episode, isLive = false) => {
    // For VOD content, startTime_utc is the current playback start time
    // For live content, it should come from the episode data
    const startTime_utc = episode.startTime_utc || (!isLive ? Date.now() : undefined);

    return {
      ...episode,
      // Map external_id to extId for analytics
      extId: episode.extId || episode.external_id || episode.id,
      // Ensure startTime_utc exists
      startTime_utc: startTime_utc,
      // Map channel fields
      channelTitle: episode.channelTitle || episode.channel_title || episode.channel || '',
      // Ensure seriesNumber exists as fallback
      seriesNumber: episode.seriesNumber || episode.series_number || episode.seasonNumber || episode.season_number || 1
    };
  }

  userActionSelectPlayableContent = (episode, isLive = false) => {
    const { extId, series, episodeNumber, seasonNumber, seriesNumber } = episode;
    const isPartialEpisode = episodeNumber && seasonNumber && !series;
    const seriesName = isLive || isPartialEpisode ? episode.name.toLowerCase() : analyticsSeriesName(series.slug || series.uri);
    const page = `player.${seriesName}.season_${seasonNumber || seriesNumber}.episode_${episodeNumber}.page`;

    let itemPosition = 1;
    let groupPosition = 1;
    let groupItemCount = 1;

    if (episode.series?.seasons) {
      for (let i = 0; i < episode.series.seasons.length; i++) {
        const season = episode.series.seasons[i];

        for (let j = 0; j < season.episodes.length; j++) {
          const ep = season.episodes[j];

          if (ep.id === episode.id) {
            itemPosition = j + 1;
            groupPosition = i + 1;
            groupItemCount = season.episodes.length;
            break;
          }
        }
      }
    }

    const eventMetadata = this.buildPageViewMetadata({
      groupName: page,
      itemName: seriesName,
      contentType: ContentType.Episode,
      extraMetadata: {
        [LabelKeys.CHAPTER_1]: isLive || isPartialEpisode ? episode.name : series.name,
        [LabelKeys.SECTION]: `${seriesName}::s${seasonNumber || seriesNumber}::e${episodeNumber}`,
        [LabelKeys.CONTENT_ID]: extId || "",
        [LabelKeys.AV_BROADCASTING_TYPE]: isLive ? Enums.avBroadcastingType.LIVE: Enums.avBroadcastingType.CLIP,
        item_link: `${location.href}?player`,
        item_position: itemPosition,
        group_position: groupPosition,
        group_item_count: groupItemCount
      }
    });

    this.userActionCustomEvent("viewability", "select", eventMetadata);
  }

  userActionTrialProgressAlert = (remainingNumDays = 0) => {
    this.pageViewTrialEnding();

    wait(() => {
      const name = `free_trial_ending_${remainingNumDays}`;
      const labels = {
        [Label.CampaignID]: CampaignID.Application
      };
      this.userActionEvent(name, ActionType.None, EventType.Impression, labels);
    });
  }

  userActionTrialStarted = () => {
    this.pageViewTrialStarted();

    wait(() => {
      const labels = {
        [Label.CampaignID]: CampaignID.Application,
        [Label.Url]: "free_trial_success"
      };
      this.userActionEvent("free_trial_success", ActionType.None, EventType.Impression, labels, true);
    });
  }

  userActionSubscribedFromTrial = (remainingNumDays = 0) => {
    this.pageViewTrialEnding();

    wait(() => {
      const name = `free_trial_ending_${remainingNumDays}`;
      const labels = {
        [Label.CampaignID]: CampaignID.Application,
        [Label.Url]: "subscribe_now"
      };
      this.userActionEvent(name, ActionType.None, EventType.Click, labels, true);
    });
  }

  userActionSignedIn = (text: string | undefined) => {
    const name = text?.replace(/ /g, "_").replace(/starhub/gi, "starhub");
    this.pageViewHome();

    wait(() => {
      const labels = {
        [Label.CampaignID]: CampaignID.Application
      };
      this.userActionCustomEvent("signin","complete",{ user_engine_name: name });
      this.userActionCustomEvent("viewability", "view", {
        ...labels,
        groupName: "signin~complete",
        itemName: name,
      });
    });
  }

  userActionSignInError = ({
    errorCode,
    errorMessage,
    errorEngine,
    errorName
  }) => {
    const errorEventPayload = {
      error_code: errorCode,
      error_message: errorMessage,
      error_engine: errorEngine,
      error_name: errorName
    }

    wait(() => {
      this.pageViewHome();
      this.userActionCustomEvent("signin", "error", errorEventPayload);
    });
  }

  userActionSignInSelection = (text: string) => {
    const name = text.replace(/ /g, "_").replace(/starhub/gi, "starhub");

    this.userActionCustomEvent("signin", "select", { user_engine_name:name })
  }

  userActionSignInDismiss = () => {
    this.userActionCustomEvent("signin", "dismiss");
  }

  userActionSubscribed = () => {
    this.pageViewSubscribed();

    wait(() => {
      const labels = {
        [Label.CampaignID]: CampaignID.Application,
        [Label.Url]: "subscribe_success"
      };
      this.userActionEvent("subscribe_success", ActionType.None, EventType.Impression, labels, true);
    });
  }

  userActionAdvancedToNextEpisode = (episode) => {
    this.setCounterName("next episode");
  }

  userActionWatchedContentPastThreshold = (episode) => {
    const normalizedEpisode = this.normalizeEpisodeData(episode, false);
    const { extId, name } = normalizedEpisode;
    const labels = {
      [Label.CampaignID]: CampaignID.Player,
      [Label.Url]: `${extId}:${name}`
    };
    this.userActionCustomEvent("viewability", "view",{...labels,name:"play_90"});
  }

  userActionRecommendationsImpression = (event_title, position) => {
    this.userActionCustomEvent("viewability", "view", {
      [Label.CampaignID]: CampaignID.Page,
      item_position: position,
      group_name: "Recommended for you",
      group_position: 2 //Player will be in first postion
    });
  }

  userActionSelectRecommendationFromRail = (position, url, railSelectionData: {
    item_name?: string;
    group_name?: string;
    group_item_count?: number | string;
    item_position?: number | string;
    group_position?: number | string;
  } = {}) => {
    this.userActionCustomEvent("viewability", "select", {
      item_name: railSelectionData.item_name || "",
      group_name: railSelectionData.group_name || "",
      group_item_count: railSelectionData.group_item_count || "",
      item_link: url,
      item_position: railSelectionData.item_position || "",
      group_position: railSelectionData.group_position || ""
    });
  }

  // CUSTOM EVENTS

  private userActionCustomEvent = (actionName: string, actionType: string, eventLabels = {}) => {
    this.setAccountOrganisation();
    try {
      const onsiteEligibleKeys = new Set([
        "personalisation",
        "metadata",
        "source",
        "result",
        "is_background"
      ]);
      const remainingLabels = Object.fromEntries(
        Object.entries(eventLabels || {}).filter(([key]) => !onsiteEligibleKeys.has(key))
      );
      const onsiteLabels = Object.fromEntries(
        Object.entries(eventLabels || {})
          .filter(([key]) => onsiteEligibleKeys.has(key))
          .map(([key, value]) => [`onsite_${key}`, value])
      );
      this.echo.userActionEvent(actionName, actionType, {
        ...remainingLabels,
        ...onsiteLabels,
        [LabelKeys.REFERRER_URL]: this.getReferrerUrl()
      });
    } catch(err) {
      console.log(err);
    }
  }

  private pageViewCustomEvent = (eventLabels = {}, isSearchPage = false) => {
    const actionName = isSearchPage ? "internal_search_result" : "viewability";
    const actionType = isSearchPage ? "display" : "view";

    this.setCounterName(actionName);
    try {
      this.echo.userActionEvent(actionName, actionType, eventLabels);
    } catch(err) {
      console.log(err);
    }
  }

  userActionNavigation = (
    navigationItem: string,
    target?: string,
    additionalData: {
      context?: typeof Analytics.navigationContextNames[keyof typeof Analytics.navigationContextNames];
      itemName?: string;
      groupName?: string;
      groupType?: string;
      sectionName?: string;
      [key: string]: any;
    } = {}
  ) => {
    const { context, itemName, groupName, groupType, sectionName, ...rest } = additionalData;
    const resolvedItemName = itemName || this.getNavigationItemName(navigationItem, context);
    const resolvedGroupName = groupName || this.getNavigationGroupName(context);
    const resolvedSectionName = sectionName || (target ? target.replace(/^\/|\/$/g, "").split("/").join("::") : "");

    const eventLabels = {
      ...rest,
      ...(resolvedGroupName ? { group_name: resolvedGroupName } : {}),
      group_type: groupType || "main",
      ...(resolvedItemName ? { item_name: resolvedItemName } : {}),
      item_link: target ? getFullUrl(getUrl(target)) : "",
      section: resolvedSectionName,
      [LabelKeys.REFERRER_URL]: this.getReferrerUrl()
    };
    this.userActionCustomEvent("click", "navigation", eventLabels);
  }

  // MEDIA EVENTS

  private getPlayPosition = () => this.player && this.player.currentTime() * 1000;

  private getLivePlayPosition = (starttime) => {
    return (new Date().getTime() - starttime);
  }

  mediaReportInit = (episode, player, isLive = false) => {
    const normalizedEpisode = this.normalizeEpisodeData(episode, isLive);

    this.setMediaPlayerAttributes();
    const { extId, series, episodeNumber, seasonNumber, seriesNumber, duration, channelTitle } = normalizedEpisode;
    const seriesName = isLive ? normalizedEpisode.name.toLowerCase() : analyticsSeriesName(series.slug || series.uri);
    this.setAvChannel(isLive, channelTitle);
    const media = new Media(Enums.AvType.VIDEO, isLive ? Enums.MediaConsumptionMode.LIVE :Enums.MediaConsumptionMode.ON_DEMAND);
    media.setEpisodeId(extId);
    media.setVersionId(extId);
    media.setAppName(this.getAppName());
    media.setAppType(Enums.ApplicationType.WEB);
    media.setName(`${seriesName}.s${seasonNumber || seriesNumber}.e${episodeNumber}`);
    media.setProducer(Enums.Producers.BBC_STUDIOS);
    media.setLength(isLive ? (duration * 60 * 1000) : (player.duration() * 1000));

    const playerDelegate = {
      getPosition: () => (isLive ? this.getLivePlayPosition(normalizedEpisode.startTime_utc) : (this.player && this.player.currentTime() * 1000) || ""),
      getTimestamp: () => (new Date()).getTime()
    };

    this.player = player;
    this.playbackStarted = false; // Reset on new media
    this.echo.setPlayerDelegate(playerDelegate);
    this.echo.setMedia(media);
  }

  mediaReportInitialized = () => this.player != undefined;

  mediaReportPlay = (isLive = false) => {
    if(!this.player) return;
    const position = isLive ? this.getLivePlayPosition(this.startTime) : this.getPlayPosition();
    this.setAccountOrganisation();
    if (!this.playbackStarted) {
      this.echo.avPlayInitiatedEvent(0);
    }
    this.echo.avPlayEvent(position);
    this.playbackStarted = true;
  }

  mediaReportPause = (isLive = false) => {
    if(!this.player) return;
    this.echo.avPauseEvent( isLive ? this.getLivePlayPosition(this.startTime) : this.getPlayPosition());
  }

  mediaReportSeeking = (position) => {
    if(!this.player) return;

    // Only send seek events after playback has started to avoid Echo warning
    if(!this.playbackStarted) return;

    this.echo.avSeekEvent(position);
  }

  mediaReportSeeked = (position) => {
    if(!this.player) return;

    // Only send seek events after playback has started to avoid Echo warning
    if(!this.playbackStarted) return;

    this.echo.avPlayEvent(position);
  }

  mediaReportEnd = (isLive = false) => {
    if(!this.player) return;
    this.setAvChannel(false);
    this.echo.avEndEvent( isLive ? this.getLivePlayPosition(this.startTime) : this.getPlayPosition());
    wait(this.mediaReportStop);
  }

  mediaReportStop = () => {
    if(!this.player) return;
    this.player = undefined;
    this.playbackStarted = false;
    this.setAvChannel(false);
    wait(() => {
      this.echo._clearMedia();
    })
  }

  mediaReportSubtitleState = (subtitled = false) => {
    if(!this.player) return;
    this.echo.setPlayerIsSubtitled(subtitled);
  }

  mediaReportError = (message: String) => {
    if(!this.player) return;
    this.echo.setPlayerError(message);
  };

  updateUrlState() {
    const newUrl = window.location.href;

    if (newUrl !== this.currentUrl) {
      this.previousUrl = this.currentUrl;
      this.currentUrl = newUrl;
    }
  }

  getReferrerUrl() {
    return this.previousUrl || "";
  }
};

const analytics = new Analytics();

// if(process.env.NODE_ENV !== "production") {
  (window as any).analytics = analytics;
  (window as any).user = User;
// }


export default analytics;
